import * as loginFormActions from "../actions/login-form.action";
import { Action, createReducer, on } from "@ngrx/store";

// reducer state model
export interface ProfileState {
    userData: any; // user who originally logged in
    viewAsStudentData: any; // only populates if teacher Views As Student (student's profile data)
    viewAsStudentCourseID: number | null;
    viewAsStudent: boolean;
    userID: number | null;
    userGUID: string | null;
    lastCourse: number | null;
    isStudent: boolean;
    isTeacher: boolean;
    isDistributor: boolean;
    beiUserID: number | null;
    accessToken: string | null;
    thumbnail: string;
    webcamStream: any;
    tempPwdDetected: boolean;
    updateProfileError: boolean;
}

// reducer initial state
export const initialState: ProfileState = {
    userData: null,
    viewAsStudentData: null,
    viewAsStudentCourseID: null,
    viewAsStudent: false,
    userID: null,
    userGUID: null,
    lastCourse: null,
    isStudent: false,
    isTeacher: false,
    isDistributor: false,
    thumbnail: "./assets/images/thumbnailDefault.png",
    webcamStream: null,
    tempPwdDetected: false,
    updateProfileError: false,
    beiUserID: null,
    accessToken: null
};

const profileReducer = createReducer(
    initialState,
    on(loginFormActions.userProfileSave, (state, { payload }) => {
        const tempPwd = payload.tempPassword ? true : false;

        return {
            ...state,
            userData: payload,
            userID: payload.userID,
            userGUID: payload.userGUID,
            beiUserID: payload.beiUserID,
            accessToken: payload.accessToken,
            lastCourse: payload.lastCourse,
            isStudent: payload.roleLevel === 5,
            isTeacher: payload.roleLevel === 4,
            isDistributor: payload.roleLevel < 4,
            thumbnail: payload.profileUrl,
            tempPwdDetected: tempPwd,
            updateProfileError: false
        };
    }),
    on(loginFormActions.userProfileUpdated, (state, { payload }) => {
        const tempPwd = payload.tempPassword ? true : false;

        return {
            ...state,
            userData: payload,
            userID: payload.userID,
            userGUID: payload.userGUID,
            beiUserID: payload.beiUserID,
            lastCourse: payload.lastCourse,
            accessToken: payload.accessToken,
            isStudent: payload.roleLevel === 5,
            isTeacher: payload.roleLevel === 4,
            isDistributor: payload.roleLevel < 4,
            thumbnail: payload.profileUrl,
            tempPwdDetected: tempPwd,
            updateProfileError: false
        };
    }),
    on(loginFormActions.setViewAsStudentProfileData, (state, { payload }) => {
        return {
            ...state,
            viewAsStudentData: payload.student,
            viewAsStudentCourseID: payload.courseID,
            viewAsStudent: true
        };
    }),
    on(loginFormActions.clearViewAsStudentProfileData, state => ({
        ...state,
        viewAsStudentData: null,
        viewAsStudentCourseID: null,
        viewAsStudent: false
    })),
    on(loginFormActions.userProfilePicSuccess, (state, { payload }) => {
        const updatedData = {
            ...state.userData,
            profileUrl: payload.imagePath
        };

        return {
            ...state,
            thumbnail: payload.imagePath,
            userData: updatedData
        };
    }),
    on(loginFormActions.userProfileUpdateAttempt, state => ({
        ...state,
        updateProfileError: false
    })),
    on(loginFormActions.userProfileUpdateSuccess, state => ({
        ...state,
        updateProfileError: false
    })),
    on(loginFormActions.userProfileUpdateFail, state => ({
        ...state,
        updateProfileError: true
    })),
    on(loginFormActions.userProfileDecrementUnreadNotificationCount, state => {
        const moddedData: any = Object.assign({}, state.userData);
        moddedData.unreadNotificationsCount =
            moddedData.unreadNotificationsCount - 1;
        return {
            ...state,
            userData: moddedData
        };
    }),
    on(
        loginFormActions.userProfileSetUnreadNotificationCount,
        (state, { payload }) => {
            const moddedData: any = Object.assign({}, state.userData);
            moddedData.unreadNotificationsCount = payload;
            return {
                ...state,
                userData: moddedData
            };
        }
    ),
    on(loginFormActions.setLastCourse, (state, { courseID }) => ({
        ...state,
        lastCourse: courseID
    })),
    on(loginFormActions.userProfileWebcam, (state, { stream }) => ({
        ...state,
        webcamStream: stream
    })),
    on(loginFormActions.http503, state => ({
        ...state,
        userData: null,
        userID: null,
        userGUID: null,
        lastCourse: null,
        isStudent: false,
        isTeacher: false,
        isDistributor: false,
        beiUserID: null,
        accessToken: null,
        thumbnail: "",
        webcamStream: null,
        tempPwdDetected: false,
        updateProfileError: false
    })),
    on(loginFormActions.http403, state => ({
        ...state,
        userData: null,
        userID: null,
        userGUID: null,
        lastCourse: null,
        isStudent: false,
        isTeacher: false,
        isDistributor: false,
        beiUserID: null,
        accessToken: null,
        thumbnail: "",
        webcamStream: null,
        tempPwdDetected: false,
        updateProfileError: false
    })),
    on(loginFormActions.userProfileResetState, state => {
        return initialState;
    }),
    on(loginFormActions.loadFidoUserDataSuccess, (state, {fidoUserData}) => {
        return {
            ...state,
            userData: {
                ...state.userData,
                firstName: fidoUserData.givenName,
                lastName: fidoUserData.surName,
                eAddress: fidoUserData.email,
            },
        };
    })
);

// exporting the reducer so it can be added to the index file
export function reducer(state: ProfileState | undefined, action: Action) {
    return profileReducer(state, action);
}

export const getUserData = (state: ProfileState) => {
    return state.viewAsStudent ? state.viewAsStudentData : state.userData;
};
export const getUserId = (state: ProfileState) => {
    return state.viewAsStudent ? state.viewAsStudentData.userID : state.userID;
};
export const getUserGUID = (state: ProfileState) => {
    return state.viewAsStudent ? state.viewAsStudentData.userGUID : state.userGUID;
};
export const getStudentLastCourse = (state: ProfileState) => state.lastCourse;
export const getIsStudent = (state: ProfileState) => state.isStudent;
export const getIsTeacher = (state: ProfileState) => state.isTeacher;
export const getViewAsStudent = (state: ProfileState) => state.viewAsStudent;
export const getViewAsStudentCourseID = (state: ProfileState) =>
    state.viewAsStudentCourseID;
export const getIsDistributor = (state: ProfileState) => state.isDistributor;
export const getBEIUserID = (state: ProfileState) => state.beiUserID;
export const getAccessToken = (state: ProfileState) => state.accessToken;
export const getUnreadNotificationsCount = (state: ProfileState) => {
    if (state && state.userData) {
        return state.userData.unreadNotificationsCount;
    }
};
export const getWebcamStream = (state: ProfileState) => state.webcamStream;
export const getTempPwdDetected = (state: ProfileState) =>
    state.tempPwdDetected;
export const getUpdateProfileError = (state: ProfileState) =>
    state.updateProfileError;
